import { useCallback, useEffect, useMemo } from 'react';

import { Dialog, DialogContent, DialogTitle, IconButton, Skeleton } from 'design-system';
import { X } from 'lucide-react';

import { isSameSymbol, isSameSymbolIgnoringPeriod, type SymbolKey } from '@/entities/symbol';
import {
  CatalogTable,
  FilterProvider,
  useCatalog,
  useForwardCatalog,
  useSpotCatalog
} from '@/features/catalog';
import { useAppForm } from '@/features/dashboard/components/AppForm/AppForm/context';
import type { TabSymbol } from '@/features/dashboard/components/AppForm/AppForm/context/types';
import { useAddToAppContext } from '@/features/dashboard/state/AppContext';
import { AppType } from '@/features/dashboard/types';
import { FactsheetDialogButton } from '@/features/factsheet';
import { clearSelection, setIsDashboardAppSelector } from '@/features/symbol-selector';
import generateSkeletonKeys from '@/shared/lib/format/generateSkeletonKeys';
import getAmountLabel from '@/shared/lib/format/getAmountLabel';
import { useAppDispatch } from '@/store/hooks';

const skeletonKeys = generateSkeletonKeys(30);

const CatalogModalBase = ({
  addItemsToBacklog,
  setOpen
}: {
  addItemsToBacklog: (items: TabSymbol[]) => void;
  setOpen: (open: boolean) => void;
}) => {
  const { form } = useAppForm();
  const type = form.state.values?.type;
  const isForwardCurves = useMemo(() => type === AppType.FWD, [type]);

  const renderFactsheetButton = useCallback(
    (code: string, mode: 'modal' | 'newTab') => (
      <FactsheetDialogButton
        code={code}
        mode={mode}
      />
    ),
    []
  );

  const {
    filterOpen,
    setFilterOpen,
    displaySettingsOpen,
    setDisplaySettingsOpen,
    table,
    rows,
    getRowPresentation,
    onRowActivate,
    expandedData,
    setExpandedData,
    forwardPeriodsInline,
    setForwardPeriodsInline,
    periodGroups,
    showDiscontinued,
    setShowDiscontinued,
    status
  } = useCatalog({ isForwardCurves, isInDashboard: true, renderFactsheetButton });

  const { data: spotCatalog } = useSpotCatalog();
  const { data: forwardCatalog } = useForwardCatalog();
  const catalog = isForwardCurves ? forwardCatalog : spotCatalog;

  const dispatch = useAppDispatch();

  // Enable special selector behavior only when Dashboard type is FWD
  useEffect(() => {
    dispatch(setIsDashboardAppSelector(true));
    return () => {
      dispatch(setIsDashboardAppSelector(false));
    };
  }, [dispatch]);

  const { setOnSubmit } = useAddToAppContext();

  const onSubmit = useCallback(
    (selectedSymbols: SymbolKey[]): void => {
      if (!selectedSymbols.length) {
        return;
      }
      const sourceSymbols = isForwardCurves
        ? Object.values(
            Object.groupBy(selectedSymbols, s => `${s.Code}|${s.PeriodType}|${s.TimeRef}`)
          ).flatMap(group => (group?.[0] ? [group[0]] : []))
        : selectedSymbols;

      const symbolsToAdd: TabSymbol[] = sourceSymbols
        .map(symbol => {
          const spot = catalog?.find(item =>
            isForwardCurves
              ? isSameSymbolIgnoringPeriod(item.Symbol, symbol)
              : isSameSymbol(item.Symbol, symbol)
          );

          if (!spot) return null;
          return {
            shortTitle: spot.Title || '',
            symbol,
            group: 'Prod_Indexes'
          };
        })
        .filter(Boolean) as TabSymbol[];

      addItemsToBacklog(symbolsToAdd);
      setOpen(false);
      dispatch(clearSelection());
    },
    [addItemsToBacklog, dispatch, isForwardCurves, setOpen, catalog]
  );

  const catalogLabel = useMemo(() => {
    if (isForwardCurves) return 'curve';
    return expandedData ? 'item' : 'code';
  }, [expandedData, isForwardCurves]);

  useEffect(() => {
    setOnSubmit(() => (selectedSymbols: SymbolKey[]) => {
      onSubmit(selectedSymbols);
    });
    return () => {
      setOnSubmit(undefined);
    };
  }, [setOnSubmit, onSubmit]);

  const handleClose = useCallback(() => {
    setOpen(false);
    dispatch(clearSelection());
  }, [dispatch, setOpen]);

  return (
    <Dialog
      open
      onOpenChange={nextOpen => {
        if (!nextOpen) handleClose();
      }}
    >
      <DialogContent
        className='flex h-[90vh] w-[90vw] max-w-none flex-col overflow-hidden p-4'
        showCloseButton={false}
      >
        <div className='mt-1 mb-6 flex shrink-0 items-center justify-between'>
          <div className='flex items-end gap-4'>
            <DialogTitle className='text-3xl font-semibold'>
              {isForwardCurves ? 'Forward curves selector' : 'Spot Selector'}
            </DialogTitle>
            <span className='text-xl leading-tight'>
              {getAmountLabel(table.getFilteredRowModel().rows.length, catalogLabel)}
            </span>
          </div>
          <IconButton
            label='Close selector'
            size='icon-lg'
            variant='ghost'
            onClick={handleClose}
          >
            <X />
          </IconButton>
        </div>
        {status === 'loading' ? (
          <div className='min-h-0 flex-1 overflow-auto'>
            {skeletonKeys.map(key => (
              <Skeleton
                key={key}
                className='my-4 h-[23px] w-full'
              />
            ))}
          </div>
        ) : (
          <div className='flex min-h-0 flex-1 flex-col'>
            <CatalogTable
              displaySettingsOpen={displaySettingsOpen}
              expandedData={expandedData}
              filterOpen={filterOpen}
              forwardPeriodsInline={forwardPeriodsInline}
              getRowPresentation={getRowPresentation}
              isForwardCurves={isForwardCurves}
              periodGroups={periodGroups}
              renderFactsheetButton={renderFactsheetButton}
              onRowActivate={onRowActivate}
              setDisplaySettingsOpen={setDisplaySettingsOpen}
              setExpandedData={setExpandedData}
              setFilterOpen={setFilterOpen}
              setForwardPeriodsInline={setForwardPeriodsInline}
              setShowDiscontinued={setShowDiscontinued}
              showDiscontinued={showDiscontinued}
              rows={rows}
              table={table}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const CatalogModal = ({
  addItemsToBacklog,
  setOpen
}: {
  addItemsToBacklog: (items: TabSymbol[]) => void;
  setOpen: (open: boolean) => void;
}) => {
  return (
    <FilterProvider disableSearchParams>
      <CatalogModalBase
        addItemsToBacklog={addItemsToBacklog}
        setOpen={setOpen}
      />
    </FilterProvider>
  );
};
